import React, {
  useState,
  ChangeEvent,
  Dispatch,
  useContext,
  useRef,
} from 'react';
import { AlertTriangleIcon, CrossIcon, EyeIcon } from 'assets/icons';
import { Button, ButtonSize, ButtonStyle } from 'common/button';
import { NotificationType } from 'common/enums';
import { Input } from 'common/input';
import { Spinner } from 'common/spinner';
import { captchaSiteKey, errorsCode } from 'config/constants';
import { appActions, AppContext } from 'context';
import CypressIds from 'cypressIds';
import { trackCustomEvent } from 'helpers/analytics';
import {
  classnames,
  getInitials,
  isPasswordValid,
  isTemporaryUser,
  noEmptyFields,
  noErrors,
} from 'helpers/utils';
import { UserController } from 'networking/controllers/user-controller';
import { Action } from 'pages/sign-up/sign-up-reducer';
import ReCAPTCHA from 'react-google-recaptcha';
import { Notification } from 'common/notification';
import { goToPage, RouteName } from 'routes';
import { UserInitials } from 'common/user-initials';
import styles from './sign-up-form.module.scss';

type SignUpProps = {
  dispatch: Dispatch<Action>;
  user: UserSignUpType;
  couponsCodeApplied: CouponCodeID[];
  token?: string;
  vaultOwner: string;
  vaultId: string;
};

type Mandatory = {
  name: string;
  email: string;
  password: string;
  repeatPassword: string;
};

const mandatoryStringFieldsErrors: Mandatory = {
  name: '',
  email: '',
  password: '',
  repeatPassword: '',
};

const SignUpForm: React.FC<SignUpProps> = ({
  user,
  dispatch,
  couponsCodeApplied,
  token,
  vaultOwner,
  vaultId,
}) => {
  const { email, password, repeatPassword } = user;
  const [errors, setErrors] = useState<Mandatory>(mandatoryStringFieldsErrors);
  const [inputPassword, setInputPassword] = useState<boolean>(true);
  const [inputPasswordRepeat, setInputPasswordRepeat] = useState<boolean>(true);
  const [showError, setShowError] = useState(false);
  const [fetching, setFetching] = useState(false);

  const captchaRef = useRef<ReCAPTCHA>(null);
  const [showCaptchaError, setShowCaptchaError] = useState<boolean>(false);

  const {
    dispatch: dispatchContext,
    state: { data: userData },
  } = useContext(AppContext);

  const validateField = (field: string, label?: string) => {
    const emptyFieldMessage =
      user[field].trim() === '' ? `${label || field} shouldn't be empty` : '';
    setErrors((prevState) => ({
      ...prevState,
      [field]: emptyFieldMessage.toUpperCase(),
    }));

    if (emptyFieldMessage) return;

    if (field === 'password') {
      const passwordMessage = !isPasswordValid(user[field])
        ? 'Invalid password. It must have at least 8 characters, one letter and one number.'
        : '';
      setErrors((prevState) => ({
        ...prevState,
        [field]: passwordMessage.toUpperCase(),
      }));

      if (passwordMessage) return;
    }

    if (field === 'repeatPassword') {
      const repeatMessage =
        user.password !== user.repeatPassword ? 'passwords do not match' : '';
      setErrors((prevState) => ({
        ...prevState,
        [field]: repeatMessage.toUpperCase(),
      }));
    }
  };

  const onChange = (
    e: ChangeEvent<HTMLInputElement>,
    objectToChange: string,
  ) => {
    dispatch({
      type: 'CHANGE_INPUT',
      object: objectToChange,
      field: e.target.name,
      value: e.target.value,
    });
  };

  const showBackendErrors = (backendErrors: any) => {
    const errorsCopy = { ...errors };

    Object.keys(errorsCopy).forEach((field) => {
      errorsCopy[field as keyof typeof mandatoryStringFieldsErrors] =
        backendErrors[field]
          ? backendErrors[field].toUpperCase()
          : errorsCopy[field as keyof typeof mandatoryStringFieldsErrors];
    });

    setErrors(errorsCopy);
  };

  const generateBackendErrors = (err: any) => {
    const emailInUse = err.errors.find(
      (e: any) => e.errorCode === errorsCode.emailInUse,
    );

    if (emailInUse) {
      const objectErrors: MandatoryFieldsType = {
        email: 'Email already in use',
      };
      showBackendErrors(objectErrors);
    }
  };

  const createUser = async () => {
    dispatchContext({ type: appActions.SESSION_EXPIRED });
    try {
      const data = await UserController.registerUser(user);
      dispatchContext({
        type: appActions.NOTIFICATION,
        notification: {
          show: true,
          title: 'Your trial clips expired.',
          message:
            'Your trial clips are only saved for a week before sign up, but we still create a free account, re-record them and get a month of free storage. No credit card required.',
          type: NotificationType.Error,
          icon: <AlertTriangleIcon />,
        },
      });
      dispatchContext({ type: appActions.USER_LOGGED, data });
      trackCustomEvent('completeSignUp');
      return true;
    } catch (e) {
      setShowError(true);
      return false;
    }
  };

  const getCouponCodesFlatArray = () =>
    couponsCodeApplied.map((sub: CouponCodeID) => sub.code);

  const registerUser = async () => {
    if (!noErrors(errors)) {
      return false;
    }
    try {
      const requestData: UserSignUpType = {
        ...user,
        couponCodes: getCouponCodesFlatArray(),
      };

      const isTemporary = isTemporaryUser(userData.user);

      let data;
      if (isTemporary) {
        data = await UserController.registerTemporaryUser(requestData);
      } else {
        data = await UserController.registerUser(requestData);
      }
      dispatchContext({ type: appActions.USER_LOGGED, data });

      return true;
    } catch (e) {
      generateBackendErrors(e);
      if (errors) {
        return false;
      }
      if (isTemporaryUser(userData.user)) {
        const success = await createUser();
        return success;
      }
      setShowError(true);
      return false;
    }
  };

  const register = async () => {
    const userRegistered = await registerUser!();

    if (!userRegistered) {
      setFetching(false);
      return;
    }

    // contributor token
    if (token) {
      const data = await UserController.me();
      dispatchContext({ type: appActions.USER_LOGGED, data });
      goToPage(RouteName.OnboardingSubjectSelection, {}, { vaultId });
    } else {
      goToPage(RouteName.EmailWaitingConfirm);
    }
  };

  const handleRegisterUser = async () => {
    setFetching(true);

    register();

    setFetching(false);
  };

  const disabledNextButton = () =>
    noEmptyFields({
      password,
      repeatPassword,
    }) &&
    noErrors(errors) &&
    user.captchaToken;

  const handleCaptcha = (e: string | null) => {
    setShowCaptchaError(false);
    if (!e) {
      return;
    }
    dispatch({
      type: 'CHANGE_INPUT',
      object: 'user',
      field: 'captchaToken',
      value: e,
    });
  };

  const showErrorNotification = () => (
    <Notification handleClose={() => setShowError(false)} />
  );

  return (
    <div className={styles.container}>
      {showError && showErrorNotification()}
      <UserInitials
        name={getInitials(
          user.companyName !== '' ? user.companyName : vaultOwner,
        )}
      />
      <div className={classnames(styles.containerTitle)}>
        <div className={classnames(styles.title, 'text__heading5__gray800')}>
          <span>{user.companyName !== '' ? user.companyName : vaultOwner}</span>
          <span className={classnames('text__body__regular__large__gray800')}>
            invited you to collaborate
          </span>
        </div>
        <div className={styles.lineSeparator} />
        <div className={styles.subtitle}>
          <span
            className={classnames(
              styles.subtitileText,
              'text__body__regular__large__gray800',
            )}
          >
            Create an account to continue
          </span>
          <span className="text__body__regular__small__gray700">
            Account for <strong>{email}</strong>
          </span>
        </div>
      </div>
      <Input
        id="password"
        name="password"
        label="Create password"
        placeholder="Create password"
        value={user.password}
        type={inputPassword ? 'password' : 'text'}
        errorMessage={errors.password}
        withIconEnd={
          <EyeIcon onClick={() => setInputPassword(!inputPassword)} />
        }
        onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e, 'user')}
        onBlur={() => {
          validateField('password');
          if (user.repeatPassword) {
            validateField('repeatPassword');
          }
        }}
        dataCy={CypressIds.passwordSignup}
        innerClassName={styles.input}
      />
      <Input
        id="repeatPassword"
        name="repeatPassword"
        label="Confirm password"
        placeholder="Confirm password"
        value={user.repeatPassword}
        type={inputPasswordRepeat ? 'password' : 'text'}
        errorMessage={errors.repeatPassword}
        withIconEnd={
          <EyeIcon
            onClick={() => setInputPasswordRepeat(!inputPasswordRepeat)}
          />
        }
        onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e, 'user')}
        onBlur={() => validateField('repeatPassword', 'Repeat Password')}
        dataCy={CypressIds.repeatPasswordSignup}
        innerClassName={styles.input}
      />
      <ReCAPTCHA
        sitekey={captchaSiteKey!}
        onChange={(e) => handleCaptcha(e)}
        ref={captchaRef}
      />
      {showCaptchaError && (
        <div className={styles.helperBox}>
          <span className={classnames(styles.iconCross)}>
            <CrossIcon className={styles.iconError} />
          </span>
          <span
            className={classnames(
              styles.errorMessage,
              'text__body__regular__overline__warning30',
            )}
          >
            Please verify that you’re not a robot
          </span>
        </div>
      )}
      <Button
        buttonStyle={ButtonStyle.PrimaryFilled}
        buttonSize={ButtonSize.Medium}
        className={classnames(
          styles.button,
          fetching ? 'noClick staticSizeRegister' : '',
        )}
        onClick={() => handleRegisterUser()}
        disabled={!disabledNextButton()}
      >
        {fetching ? (
          <Spinner color="white" className="spinner-inside-button" />
        ) : (
          <span>Create account</span>
        )}
      </Button>
    </div>
  );
};

export { SignUpForm };
